import { useState, useEffect, useRef, useCallback, type ReactElement } from 'react';
import { Plus, HelpCircle, X } from 'lucide-react';
import { InlineSpinner } from '@/components/Spinner';
import { MultiTargetCommentTag, type CommentDeleteTarget } from '@/components/MultiTargetCommentTag';
import { PopoverShell } from '@/components/PopoverShell';
import { RowActionButton } from '@/pages/influencers/components/RowActionButton';
import ChannelTypeIcon from '@/components/icons/ChannelTypeIcon';
import { ChannelThumbnail } from '@/components/ChannelThumbnail';
import Tooltip from '@/components/Tooltip';
import {
  loadOldCommentTokens,
  loadRecommendedCommentTokens,
  saveCommentTokens,
} from '@/lib/api/campaign.api';
import { useI18nText } from '@/lib/utils/i18nText';

function inclusionCommentToTokens(comment?: string): string[] {
  if (!comment) return [];
  return comment.split(',').map((s) => s.trim()).filter(Boolean);
}

/**
 * Self-contained "Edit inclusion comment" popover — renders its own trigger
 * button (like EmailCell/EstPriceCell/TagEditPopover) and anchors to it via the
 * shared `PopoverShell` (Radix Popover: collision-aware placement, focus
 * trap, Esc/outside-click-to-close) instead of a fixed-center full-screen
 * modal, with the same thumbnail + platform icon + name + close (✕) identity
 * header as every other channel-row popover.
 *
 * campaignId: 'default' → saves user.suggestedComment (requires toAccessAllFeatures)
 * campaignId: <objectId> → saves campaign-specific userCampaign.comment
 *
 * Every add/remove persists immediately (no manual Save step). Edits are
 * serialized through `persistTokens`/`flushSave` so a save already in
 * flight is followed by one more save with the latest token set, instead
 * of firing a request per keystroke/click.
 */
export function InclusionCommentEditor({
  userId, channel, displayName, comment, campaignId, applyToSiblings, blockedTokens, onRemoveFromTargets, onSaved,
  trigger,
}: {
  userId: string;
  /** Identity header thumbnail + platform icon. */
  channel?: { thumbnail?: string; type?: string };
  displayName: string;
  comment?: string;
  campaignId: string;
  applyToSiblings?: boolean;
  /** Tokens shown alongside this comment in the cell that live in *other* campaigns
   *  (not this editor's target). Shown here with their own delete chooser so they can
   *  be removed without leaving the popover. */
  blockedTokens?: { token: string; targets: CommentDeleteTarget[] }[];
  /** Deletes `token` from the given campaign targets (owned by the parent cell). */
  onRemoveFromTargets?: (token: string, campaignIds: string[]) => Promise<void>;
  onSaved: (newComment: string | null) => void;
  /** Trigger element — defaults to the table row's usual "Edit" action button.
   *  Override for non-table contexts (e.g. the panel page's secondary button). */
  trigger?: ReactElement;
}) {
  const { t } = useI18nText();
  const [open, setOpen] = useState(false);
  const [tokens, setTokens] = useState<string[]>(inclusionCommentToTokens(comment));
  const [inputValue, setInputValue] = useState('');
  const [recommended, setRecommended] = useState<{
    mostPopular: string[]; influencerComments: string[]; loading: boolean;
  }>({ mostPopular: [], influencerComments: [], loading: true });
  const [saving, setSaving] = useState(false);

  const inputRef = useRef<HTMLInputElement>(null);
  const lastSavedRef = useRef<string[]>(inclusionCommentToTokens(comment));
  const pendingRef = useRef<string[] | null>(null);
  const savingRef = useRef(false);

  useEffect(() => {
    if (!open) return;
    const initial = inclusionCommentToTokens(comment);
    setTokens(initial);
    lastSavedRef.current = initial;
    pendingRef.current = null;
    setInputValue('');
    // Land the cursor in the input as soon as the popover opens.
    inputRef.current?.focus();

    loadOldCommentTokens().catch(() => []);

    setRecommended((p) => ({ ...p, loading: true }));
    loadRecommendedCommentTokens(userId).then((data) => {
      setRecommended({
        mostPopular: data?.mostPopularCommentsFromPreviousCampaigns ?? [],
        influencerComments: data?.influencerCommentsFromPreviousCampaigns ?? [],
        loading: false,
      });
    });
  }, [open, userId, comment]);

  const flushSave = useCallback(async () => {
    if (savingRef.current) return;
    const toSave = pendingRef.current;
    if (toSave === null) return;
    pendingRef.current = null;
    savingRef.current = true;
    setSaving(true);
    try {
      const newComment = await saveCommentTokens(userId, toSave, {
        campaignId,
        applyToSiblings: applyToSiblings ?? false,
      });
      lastSavedRef.current = toSave;
      onSaved(newComment);
    } catch {
      // global error handler already surfaced the failure; drop the
      // unsaved change so displayed tokens don't drift from the server
      pendingRef.current = null;
      setTokens(lastSavedRef.current);
    } finally {
      savingRef.current = false;
      setSaving(false);
      if (pendingRef.current !== null) flushSave();
    }
  }, [userId, campaignId, applyToSiblings, onSaved]);

  const persistTokens = useCallback((next: string[]) => {
    pendingRef.current = next;
    flushSave();
  }, [flushSave]);

  const addToken = (tk: string) => {
    setTokens((prev) => {
      if (prev.includes(tk)) return prev;
      const next = [...prev, tk];
      persistTokens(next);
      return next;
    });
  };

  const removeToken = (tk: string) => {
    setTokens((prev) => {
      if (!prev.includes(tk)) return prev;
      const next = prev.filter((x) => x !== tk);
      persistTokens(next);
      return next;
    });
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && inputValue.trim()) {
      e.preventDefault();
      addToken(inputValue.trim());
      setInputValue('');
    }
  };

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      const pending = inputValue.trim();
      if (pending) {
        addToken(pending);
        setInputValue('');
      }
    }
    setOpen(next);
  };

  const TagButton = ({ tk }: { tk: string }) => {
    const included = tokens.includes(tk);
    return (
      <button
        onClick={() => addToken(tk)}
        className={`mr-1.5 mb-1.5 inline-flex items-center gap-1 rounded-ggc-sm border px-1 py-0.5 text-[10px] transition ${
          included
            ? 'border-ggc-green bg-ggc-green-light text-ggc-green'
            : 'border-ggc-border-gray-dark bg-white text-ggc-text-gray hover:border-ggc-border-gray-dark'
        }`}
      >
        <Plus className="h-2 w-2" />{tk}
      </button>
    );
  };

  const helpText = t(
    'These are shown to the advertiser alongside this influencer as a reason/inclusion note.',
    '광고주에게 이 인플루언서와 함께 노출되는 선정 사유/코멘트입니다.',
  );

  return (
    <PopoverShell
      open={open}
      onOpenChange={handleOpenChange}
      trigger={trigger ?? <RowActionButton className="mt-1">{t('Edit', '편집')}</RowActionButton>}
      side="bottom"
      align="start"
      width={480}
      maxHeight="var(--radix-popper-available-height, 560px)"
      resizable
    >
      {/* height (not just maxHeight) also inherited so the body below can actually grow
          into whatever extra room a corner-drag resize adds — see PopoverShell's
          `resizable` doc comment. */}
      <div className="flex flex-col" style={{ maxHeight: 'inherit', height: 'inherit' }}>
        {/* Identity header — same thumbnail + platform icon + name + close (✕)
            treatment as every other channel-row popover (EstPriceCell's
            Channel Rates, CampaignHistoryPopover, TagEditPopover). */}
        <div className="est-price-popover-header">
          <ChannelThumbnail src={channel?.thumbnail} />
          <ChannelTypeIcon type={channel?.type ?? 'youtube'} size={14} asBadge />
          <span className="est-price-popover-name">{displayName}</span>
          <Tooltip content={helpText} placement="left">
            <span className="flex cursor-help items-center text-ggc-text-gray">
              <HelpCircle className="h-3.5 w-3.5" />
            </span>
          </Tooltip>
          {saving && (
            <span className="flex items-center gap-1 ggc-text-caption-s text-ggc-text-gray">
              <InlineSpinner size={12} />
            </span>
          )}
          <button
            type="button"
            className="est-price-popover-close"
            onClick={() => handleOpenChange(false)}
            aria-label={t('Close', '닫기')}
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>

        <div className="overflow-y-auto p-3">
          <div className="mb-3 flex flex-wrap items-center gap-1 rounded-ggc-md border border-ggc-border-gray bg-white px-3 py-2 transition-colors focus-within:border-ggc-blue focus-within:ring-2 focus-within:ring-ggc-blue/10">
            {tokens.map((tk) => (
              <span key={tk} className="inline-flex items-center gap-1 rounded-ggc-sm bg-ggc-bg-base px-2 py-0.5 text-xs">
                {tk}
                <button onClick={() => removeToken(tk)} className="text-ggc-disabled hover:text-ggc-text-primary">×</button>
              </span>
            ))}
            <input
              ref={inputRef}
              className="min-w-[100px] flex-1 border-none bg-transparent px-1 py-0.5 text-[12px] text-ggc-main outline-none placeholder:text-ggc-text-gray-light"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={t('Type and press Enter...', '입력 후 Enter')}
            />
          </div>

          {blockedTokens && blockedTokens.length > 0 && onRemoveFromTargets && (
            <div className="mb-3">
              <h4 className="mb-1 ggc-text-section-header">
                {t('Comments from other campaigns', '다른 캠페인의 코멘트')}
              </h4>
              <p className="mb-2 ggc-text-caption-s text-ggc-text-gray">
                {t(
                  'These belong to specific campaigns — click × to delete them from the campaign they’re in.',
                  '특정 캠페인에 속한 코멘트입니다 — × 를 눌러 해당 캠페인에서 바로 삭제할 수 있습니다.',
                )}
              </p>
              <div className="flex flex-wrap gap-1.5">
                {blockedTokens.map((b) => (
                  <MultiTargetCommentTag
                    key={b.token}
                    token={b.token}
                    targets={b.targets}
                    onRemove={(cids) => onRemoveFromTargets(b.token, cids)}
                  />
                ))}
              </div>
            </div>
          )}

          {recommended.loading ? (
            <div className="flex w-full min-h-24 items-center justify-center"><InlineSpinner /></div>
          ) : (
            <>
              {recommended.mostPopular.length > 0 && (
                <div className="mb-3">
                  <h4 className="mb-2 ggc-text-section-header">
                    {t('10 most popular comments from previous campaigns', '이전 캠페인에서 가장 인기 있는 10개 댓글')}
                  </h4>
                  <div className="flex flex-wrap">
                    {recommended.mostPopular.map((tk) => <TagButton key={tk} tk={tk} />)}
                  </div>
                </div>
              )}
              {recommended.influencerComments.length > 0 && (
                <div className="mb-3">
                  <h4 className="mb-2 ggc-text-section-header">
                    {t("Influencer's comments from previous campaigns", "이전 캠페인에서 인플루언서의 댓글")}
                  </h4>
                  <div className="flex flex-wrap">
                    {recommended.influencerComments.map((tk) => <TagButton key={tk} tk={tk} />)}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </PopoverShell>
  );
}
